// Bootstrap-failure handling (O2, plans/boot-health.md Step 23, #4027).
//
// Problem: main.ts's `bootstrapModule(AppModule).catch` used to be a bare
// console.error. A boot that dies before Angular exists left the user staring
// at a frozen splash forever and left us ZERO telemetry — the worst class of
// boot failure was also the most invisible one.
//
// This module gives that catch three best-effort duties, in execution order:
// 1. Store the rejection into the Step-22 web boot marker
//    (recordBootstrapFailure), so the NEXT successful boot reports it as
//    WebBootIncomplete with last_stage 'bootstrap-failed' + the error text.
// 2. Render a minimal static error UI with plain DOM (no Angular, no theme —
//    neither can be trusted at this point) so the user gets words and a
//    Reload button instead of an eternal splash. The copy is the English
//    fallback: the typesafe-i18n layer boots WITH Angular, so no translation
//    system exists here (the app ships English-only today — en.json is the
//    single locale).
// 3. Fire one raw Sentry envelope to our /api/e tunnel via fetch — no SDK
//    machinery, hand-built bytes (see bootstrap-failure-envelope.ts for why).
//    Last on purpose: the durable duties must not wait on the network.
//
// Every duty is individually guarded: this is post-mortem code running in a
// broken app, and it must never make things worse. Failures inside it go to
// the console only — there is no working reporting channel left to use.

import { DateTime } from 'luxon';
import { AppEnvironment } from 'environments/environment';
import { isCapacitorNative } from 'environments/environment-helpers';
import { getUnversionedRelayOrigin } from 'util/backend-origin';
import { getSentryReleaseVersion } from 'environments/version-getters';
import { buildBootstrapFailureEnvelope, deriveBootstrapErrorText } from './bootstrap-failure-envelope';
import { recordBootstrapFailure } from './web-boot-marker';

// Duplicated from app-sentry.ts's Sentry.init on purpose: importing app-sentry
// here would couple this last-resort module to the whole SDK init. A tripwire
// in bootstrap-failure.test.ts asserts the two literals stay identical.
const SENTRY_DSN = 'https://690342c4a07e42b7b2026feef69d9bee@o202844.ingest.us.sentry.io/1317290';

// the English fallback copy (see the header for why it is not translated)
const ERROR_TITLE = 'The app could not start';
const ERROR_BODY = 'Something went wrong while loading. Reloading usually fixes this. If it keeps happening, please contact support.';
const RELOAD_LABEL = 'Reload';

// turns a caught value into text for the marker and the beacon, and never throws:
// a pathological value whose String()/toString itself throws must not skip a duty
function printableErrorText(value: unknown): string {
  try {
    return deriveBootstrapErrorText(value);
  } catch {
    return '(unprintable error value)';
  }
}

// handles a bootstrapModule rejection: marker, then UI, then beacon. async so
// the beacon can be awaited by tests; main.ts fires it void.
export async function handleBootstrapFailureAsync(err: unknown): Promise<void> {
  const errorText = printableErrorText(err);

  // what duties 1 and 2 threw, if they did. Carried by the beacon in duty 3, the
  // one place left that can tell us about them (#5191).
  let markerError: unknown;
  let uiRenderError: unknown;

  // duty 1: persist into the Step-22 marker so the next boot reports it.
  // web only — native bootstrap deaths are covered by the FreezeWatchdog's
  // boot log, and the marker is only captured/cleared on the web path.
  try {
    if (!isCapacitorNative()) {
      recordBootstrapFailure(errorText, DateTime.utc().toISO());
    }
  } catch (markerErr: any) {
    // expected: storage blocked/full (SecurityError / QuotaExceededError).
    // the beacon below carries it
    console.warn('BootstrapFailure: could not store the boot marker:', markerErr?.message);
    markerError = markerErr;
  }

  // duty 2: render the static error UI before the (slow) network attempt
  try {
    renderBootstrapFailureUi(document);
  } catch (uiErr: any) {
    // the beacon below carries it: the person may be looking at a frozen splash
    console.warn('BootstrapFailure: could not render the error UI:', uiErr?.message);
    uiRenderError = uiErr;
  }

  // duty 3: one best-effort raw envelope to the Sentry tunnel
  try {
    // DateTime.utc() is always valid, so toISO() never returns Luxon's
    // invalid-date null here — the code relies on that runtime guarantee
    const now = DateTime.utc();
    const envelope = buildBootstrapFailureEnvelope({
      dsn: SENTRY_DSN,
      release: getSentryReleaseVersion(),
      environment: AppEnvironment.name,
      errorText,
      eventId: crypto.randomUUID().replace(/-/g, ''),
      sentAtIso: now.toISO(),
      unixSeconds: Math.floor(now.toSeconds()),
      markerError: markerError === undefined ? undefined : printableErrorText(markerError),
      uiRenderError: uiRenderError === undefined ? undefined : printableErrorText(uiRenderError),
    });
    // keepalive lets the request survive the user hitting Reload immediately.
    // The relay accessor, not the waiting one (#4405): this is post-mortem code
    // in an app that has already failed to start, so it must not wait on
    // anything — and /api/e relays to the same Sentry project from either
    // server, so the choice of host cannot change where the event lands.
    await fetch(`${getUnversionedRelayOrigin()}/api/e`, {
      method: 'POST',
      body: envelope,
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    });
  } catch (beaconErr: any) {
    // expected: offline / tunnel unreachable (TypeError from fetch), or a
    // TypeError from crypto.randomUUID in a non-secure context — either way
    // the marker (duty 1) is the durable fallback and reports on the next boot
    console.warn('BootstrapFailure: beacon not delivered:', beaconErr?.message);
  }
}

// replaces the page content with a plain-DOM error card. inline styles only —
// the app's theme pipeline never loaded. (box-shadow instead of border per the
// project styling rule, even down here.)
function renderBootstrapFailureUi(doc: Document): void {
  const container = doc.createElement('div');
  container.setAttribute('style', [
    'position:fixed', 'inset:0', 'display:flex', 'flex-direction:column',
    'align-items:center', 'justify-content:center', 'gap:12px',
    'background:#fff', 'color:#222',
    'font-family:-apple-system,system-ui,sans-serif', 'padding:24px',
    'text-align:center', 'z-index:99999',
  ].join(';'));

  const title = doc.createElement('h1');
  title.textContent = ERROR_TITLE;
  title.setAttribute('style', 'font-size:20px;margin:0');

  const body = doc.createElement('p');
  body.textContent = ERROR_BODY;
  body.setAttribute('style', 'font-size:14px;max-width:340px;margin:0');

  const button = doc.createElement('button');
  button.textContent = RELOAD_LABEL;
  button.setAttribute('style', [
    'font-size:16px', 'padding:10px 28px', 'border-radius:8px',
    'background:#1a73e8', 'color:#fff', 'cursor:pointer',
    // border:none only RESETS the user-agent's default button border — no
    // border is drawn; the visible ring comes from box-shadow, per the
    // project's never-use-CSS-border rule
    'border:none',
    'box-shadow:0 0 0 1px inset rgba(0,0,0,0.05)',
  ].join(';'));
  // reload through the injected document's window so the doc param stays a
  // real seam (falls back to the global for a detached test document)
  button.addEventListener('click', () => (doc.defaultView ?? window).location.reload());

  container.appendChild(title);
  container.appendChild(body);
  container.appendChild(button);
  doc.body.appendChild(container);
}
